/**
 * Per-SOURCE rules for the three tax/price display elements. These are applied IDENTICALLY on
 * every surface that can render them — listing card, detail page, guest-details sidebar, payment
 * sidebar, booking-confirmation page, and emails — so a given supplier shows the SAME thing
 * everywhere. The gating is SOURCE-driven, never data-driven: e.g. RateHawk never shows the
 * "(Includes taxes and charges)" line even if it happens to carry VAT data, and WebHotelier never
 * shows a tax list even if a tax ever appeared.
 *
 * Matrix (source → element). NOTE: the "Includes X% VAT - {amount}" line is REMOVED from the UI
 * everywhere (product, 2026-06-30) — `showsVatStatement` always returns false now; data is retained.
 *   BookingClap : "(Includes taxes and charges)"                                     — NO tax list, (VAT line removed)
 *   WebHotelier : "(Includes taxes and charges)"                                     — NO VAT line, NO tax list
 *   RateHawk    : tax-list (i) tooltip (VAT excluded via excludeVatTaxes)            — NO charges line, NO VAT line
 *   HyperGuest  : tax-list (i) tooltip (VAT excluded via excludeVatTaxes)            — NO charges line, NO VAT line
 *
 * `source` is the cart/record/detail source string ('BookingClap' | 'RateHawk' | 'HyperGuest' |
 * 'WebHotelier'); the RateHawk CUG feed ('ratehawk-cug') counts as RateHawk. Case-insensitive.
 */
function normSource(source?: string | null): string {
  return (source || '').trim().toLowerCase();
}

/** Tax-list (i) tooltip — RateHawk (incl. CUG) + HyperGuest only. VAT is excluded from the list separately. */
export function showsTaxList(source?: string | null): boolean {
  const s = normSource(source);
  return s === 'ratehawk' || s === 'ratehawk-cug' || s === 'hyperguest';
}

/** "(Includes taxes and charges)" line — BookingClap + WebHotelier only. */
export function showsIncludesTaxesAndCharges(source?: string | null): boolean {
  const s = normSource(source);
  return s === 'bookingclap' || s === 'webhotelier';
}

/**
 * "Includes X% VAT - {amount}" statement.
 *
 * REMOVED FROM THE UI (product, 2026-06-30): the VAT line is no longer surfaced on ANY frontend
 * surface or in emails — always returns `false`. The VAT DATA is still computed and stored on the
 * cart (`vatAmount`, `vatAmountInUserCurrency`, `finalTaxList`) for the CMS grids / partner portal;
 * only the customer-facing line is hidden. (Was BookingClap-only.) Flip back here to restore.
 */
export function showsVatStatement(_source?: string | null): boolean {
  return false;
}

/**
 * Itemized price-breakdown card (ex-VAT price / BIP commission / cost commission / total tax /
 * total ex-tax) — BookingClap only. The underlying fields are computed for every source
 * (`cart.finalRatesObj.totalPriceExTax`/`totalTax`/etc.), but the breakdown UI is shown only here.
 */
export function showsPriceBreakdown(source?: string | null): boolean {
  return normSource(source) === 'bookingclap';
}
